import styled from 'styled-components';
import { useCallback } from 'react';
import {
  FilterActions,
  FilterState,
} from '@paradigm/features/src/leaderboard/contexts/filter-provider-factory';

import {
  findPresetByDate,
  getPresets,
} from '@paradigm/features/src/leaderboard/contexts/date-presets';
import {
  VENUE_OPTIONS,
  CURRENCY_OPTIONS,
  INSTRUMENT_KINDS_VOLUMES_OPTIONS,
  PROTOCOL_OPTIONS,
  STRATEGY_OPTIONS,
  INSTRUMENT_KINDS_LIQUIDITY_OPTIONS,
  PROTOCOL_OPTIONS_VOLUMES,
} from '@paradigm/features/src/leaderboard/entities/shared';
import {
  RangeDatePicker,
  RangeDatePreset,
} from '@paradigm/design-system/src/components/DatePicker';
import Select, {
  SelectOption,
} from '@paradigm/design-system/src/components/Select';
import { MOBILE_BREAKPOINT } from '#/containers/layout/constants';
import { RangeDatePickerProps } from '@paradigm/design-system/src/components/DatePicker/RangeDatePicker';

type FilterType = 'volumes' | 'liquidity';

export interface FilterFormProps {
  readonly actions: FilterActions;
  readonly state: FilterState;
  readonly hasStrategyField?: boolean;
  readonly lastUpdatedAt: number | null;
  readonly filterType: FilterType;
}

export default function FilterForm({
  actions,
  state,
  hasStrategyField = false,
  lastUpdatedAt,
  filterType = 'volumes',
}: FilterFormProps) {
  const { selectDates, selectOption } = actions;
  const {
    start,
    end,
    currencies,
    venues,
    instrumentKinds,
    protocols,
    strategies,
  } = state;

  const onChangeDate: RangeDatePickerProps['onChange'] = useCallback(
    (rangeDates) => {
      if (rangeDates == null) {
        selectDates(null);
        return;
      }
      const datePresetLabel = findPresetByDate(rangeDates, lastUpdatedAt);

      selectDates(rangeDates, datePresetLabel?.label ?? null);
    },
    [lastUpdatedAt, selectDates],
  );

  return (
    <Container>
      <RangeDatePicker
        onChange={onChangeDate}
        dataTestId="filter-form-select-period"
        value={getDateValue(start, end)}
        label="Date"
        placeholder={['All Time', '']}
        presets={getPresets(lastUpdatedAt)}
        format="DD MMM YY"
      />
      <Select
        isMulti
        label="Venue"
        variant="material"
        data-testid="filter-form-select-venue"
        value={getSelectValue(VENUE_OPTIONS, venues)}
        onChange={(value) => selectOption('venues', value)}
        options={VENUE_OPTIONS}
      />
      <Select
        isMulti
        variant="material"
        label="Currency"
        data-testid="filter-form-select-currency"
        value={getSelectValue(CURRENCY_OPTIONS, currencies)}
        onChange={(value) => selectOption('currencies', value)}
        options={CURRENCY_OPTIONS}
      />
      {filterType === 'volumes' && (
        <Select
          isMulti
          variant="material"
          label="Instrument"
          data-testid="filter-form-select-instrument"
          value={getSelectValue(
            INSTRUMENT_KINDS_VOLUMES_OPTIONS,
            instrumentKinds,
          )}
          onChange={(value) => selectOption('instrumentKinds', value)}
          options={INSTRUMENT_KINDS_VOLUMES_OPTIONS}
        />
      )}
      {filterType === 'liquidity' && (
        <Select
          isMulti
          variant="material"
          label="Instrument"
          data-testid="filter-form-select-instrument"
          value={getSelectValue(
            INSTRUMENT_KINDS_LIQUIDITY_OPTIONS,
            instrumentKinds,
          )}
          onChange={(value) => selectOption('instrumentKinds', value)}
          options={INSTRUMENT_KINDS_LIQUIDITY_OPTIONS}
        />
      )}
      {hasStrategyField && (
        <Select
          isMulti
          variant="material"
          label="Strategy"
          data-testid="filter-form-select-strategy"
          value={getSelectValue(STRATEGY_OPTIONS, strategies)}
          onChange={(value) => selectOption('strategies', value)}
          options={STRATEGY_OPTIONS}
        />
      )}
      {filterType === 'volumes' && (
        <Select
          isMulti
          variant="material"
          label="Liquidity Protocol"
          data-testid="filter-form-select-liquidity-protocol"
          value={getSelectValue(PROTOCOL_OPTIONS_VOLUMES, protocols)}
          onChange={(value) => selectOption('protocols', value)}
          options={PROTOCOL_OPTIONS_VOLUMES}
        />
      )}
      {filterType === 'liquidity' && (
        <Select
          isMulti
          variant="material"
          label="Liquidity Protocol"
          data-testid="filter-form-select-liquidity-protocol"
          value={getSelectValue(PROTOCOL_OPTIONS, protocols)}
          onChange={(value) => selectOption('protocols', value)}
          options={PROTOCOL_OPTIONS}
        />
      )}
    </Container>
  );
}

function getSelectValue<T extends string>(
  options: SelectOption<T>[],
  values: readonly T[],
) {
  return options.filter((o) => values.includes(o.value));
}

function getDateValue(
  start: number | null,
  end: number | null,
): RangeDatePreset['value'] {
  return [
    start == null ? null : new Date(start),
    end == null ? null : new Date(end),
  ];
}

const Container = styled.div`
  border-radius: 7px;
  background-color: #07071d;
  display: grid;
  grid-auto-flow: column;
  grid-auto-columns: minmax(0, 1fr);
  gap: 20px;
  align-items: center;
  padding: 16px;
  margin: 0 1rem;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    background-color: transparent;
    grid-auto-flow: row;
    padding-top: 1.25rem;
  }
`;
